import React, { useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import axios from "axios";
import { API_BASE_URL } from '../config';
import { SelectButton } from 'primereact/selectbutton';

interface OnDemandDialogProps {
    visible: boolean;
    onHide: () => void;
    projectId: String
}

const OnDemandDialog: React.FC<OnDemandDialogProps> = ({ visible, onHide, projectId }) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [email, setEmail] = useState(""); // Track email address
    const [selectedDate, setSelectedDate] = useState<Date | null>(null); // Track selected date
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false); // Control visibility of date picker
    const calendarRef = useRef<any>(null); // Reference to the Calendar component
    const options = [
        { label: "Jobcards", value: "jc" },
        { label: "Manning Profile", value: "mp" },
        { label: "Planning", value: "planning" },
        { label: "Task Template", value: "tasks" },
        { label: "Timewriting", value: "tw" },
        {label : "Daily Report", value: "dr"}
    ];

    const handleDownload = async () => {
        if (!selectedOption) {
            alert("Please select a template option.");
            return;
        }
        if (!email) {
            alert("Please enter an email address.");
            return;
        }

        // Date is optional for Daily Report
        let dateString = null;

        if (selectedOption === "dr" && selectedDate) {
            // Convert date to string format "YYYY-MM-DD"
            const year = selectedDate.getFullYear();
            // getMonth() is 0-indexed, so add 1
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            dateString = `${year}-${month}-${day}`;
        }
        const postData = {
            email,  // Email input
            templates: selectedOption,  // Selected template
            project_id: projectId,  // Example project ID,
            selected_date: dateString, // Date for Daily Report
        };

        try {
            // POST request to send data (Email + Template)
            const response = await axios.post(`${API_BASE_URL}/ermeo/ondemand-template`, postData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 200) {
                setSelectedOption(null);
                setEmail("");
                setSelectedDate(null);
                setShowDatePicker(false);
                onHide();  // Close the dialog on success
            } else {
                console.error("POST request failed:", response.statusText);
                alert("Failed to send template.");
            }
        } catch (error) {
            console.error("Error during request:", error);
            alert("An error occurred while sending the template.");
        }
    };
    return (
        <Dialog
            header="Select Template"
            visible={visible}
            style={{ width: "90vw", maxWidth: "500px" }}
            modal
            onHide={onHide}
            footer={
                <div>
                    <Button label="Cancel" icon="pi pi-times" onClick={onHide} />
                    <Button
                        label="Download"
                        icon="pi pi-check"
                        className="p-button-success"
                        onClick={handleDownload}
                    />
                </div>
            }
        >
            <div className="p-field-radiobutton">
                {options.map((option) => (
                    <div key={option.value} style={{ marginBottom: "10px" }}>
                        <RadioButton
                            inputId={option.value}
                            name="template"
                            value={option.value}
                            onChange={(e) => setSelectedOption(e.value)}
                            checked={selectedOption === option.value}
                        />
                        <label htmlFor={option.value} style={{ marginLeft: "10px" }}>
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
            <div className="p-field">
                <label htmlFor="email" style={{ marginBottom: "10px" }}>
                    Email Address
                </label>
                <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="p-inputtext p-component"
                    style={{ width: "100%" }}
                />
            </div>
            
            {/* Date picker that appears only when Daily Report is selected */}
            {selectedOption === "dr" && (
                <div className="p-field" style={{ marginTop: "15px" }}>
                    <div style={{ marginBottom: "10px" }}>
                        <label htmlFor="date">
                            Select Date (optional)
                        </label>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Calendar
                            id="date"
                            ref={calendarRef}
                            value={selectedDate}
                            onChange={(e) => {
                                // Check if e.value is defined before using it
                                if (e.value !== undefined) {
                                    // Update the selected date
                                    setSelectedDate(e.value as Date);
                                }
                            }}
                            dateFormat="yy-mm-dd"
                            placeholder="Select a date"
                            showIcon
                            style={{ width: "100%" }}
                            selectionMode="single"
                            readOnlyInput={false}
                            inputStyle={{ cursor: 'pointer' }}
                            panelClassName="calendar-overlay"
                            appendTo={document.body} // Attach to body to display over the dialog
                            onShow={() => {
                                setShowDatePicker(true);
                                // Add CSS to ensure the calendar overlay displays correctly
                                setTimeout(() => {
                                    const calendar = document.querySelector('.calendar-overlay');
                                    const inputEl = document.getElementById('date');
                                    
                                    if (calendar && calendar instanceof HTMLElement && inputEl) {
                                        // Get the input element's position relative to the viewport
                                        const rect = inputEl.getBoundingClientRect();
                                        
                                        // Set the calendar position to be fixed relative to the viewport
                                        calendar.style.position = 'fixed';
                                        calendar.style.zIndex = '9999';
                                        
                                        // Position the calendar above the input field
                                        // We use the viewport coordinates (rect.top, rect.left) since we're using position: fixed
                                        const calendarHeight = calendar.offsetHeight || 300;
                                        calendar.style.top = `${rect.top - calendarHeight - 5}px`; // 5px margin
                                        calendar.style.left = `${rect.left}px`;
                                    }
                                }, 50); // Give it a bit more time to ensure the calendar is rendered
                            }}
                            onHide={() => setShowDatePicker(false)}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default OnDemandDialog;
